import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import App from './App'
import { validateEnv } from './utils/envValidation'
import { initGA4 } from './utils/analytics'
import './index.css'

// Auto-reload when a lazy-loaded chunk fails to fetch after a new deploy.
// Vite renames chunks on every build — old tabs hold stale URLs that 404.
// One reload fetches the current deploy's chunks. The sessionStorage flag
// prevents an infinite reload loop if the error persists after the refresh.
window.addEventListener('unhandledrejection', (event) => {
  const msg = event.reason?.message ?? ''
  if (msg.includes('Failed to fetch dynamically imported module')) {
    if (!sessionStorage.getItem('chunk_reload_attempted')) {
      sessionStorage.setItem('chunk_reload_attempted', '1')
      window.location.reload()
    }
  }
})

// Validate environment variables on startup (silently in dev, report to Sentry in prod)
const envValidation = validateEnv()
if (!envValidation.valid && import.meta.env.PROD) {
  // Only report in production - development uses fallbacks
  Sentry.captureMessage(`Missing env vars: ${envValidation.missing.join(', ')}`, 'warning')
}

// Initialize Sentry for error monitoring (only if DSN is configured)
const sentryDsn = import.meta.env.VITE_SENTRY_DSN
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
}

// Initialize Google Analytics 4 (only if measurement ID is configured)
initGA4()

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>,
)
